import { createContext, DragEvent as ReactDragEvent, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { useRealtime } from '@/features/realtime/realtime';
import * as api from '@/features/backlog/api';
import { Item } from '@/features/backlog/types';
import { hiddenFromBoard } from '@/features/backlog/constants';
import { useStatusMeta } from '@/features/backlog/statuses';
import { isClosedKind, statusKind } from '@/components/status-dot';

/** An epic's completion, from its linked items' statuses: `closed` counts done AND dropped items (no
 *  work left on either), `dropped` the dropped ones among them (Dropped, Won't fix, Archived…). */
export interface EpicProgress {
  closed: number;
  dropped: number;
  total: number;
}

/** Sentinel `focusedEpicId` value meaning "focus the items with NO epic" (the Unassigned rail card).
 *  It's not a real item id, so create/link paths must translate it to `null` (see focusedEpicForCreate). */
export const UNASSIGNED_EPIC = '__unassigned__';
/** URL-friendly value standing in for {@link UNASSIGNED_EPIC} in the `?epic=` query param. */
const EPIC_PARAM_UNASSIGNED = 'unassigned';

/** Whether an item matches the current epic focus — the shared predicate behind every view's
 *  dim/hide logic. `focused` is an epic id, {@link UNASSIGNED_EPIC}, or null (no focus). */
export function epicFocusMatches(itemEpicId: string | null | undefined, focused: string | null): boolean {
  if (focused === UNASSIGNED_EPIC) return !itemEpicId; // "unassigned" = has no epic link
  return (itemEpicId ?? null) === focused;
}

/** The epic a newly-created item should adopt given the current focus: the focused epic, or null when
 *  nothing (or "Unassigned") is focused — so creating while filtered on Unassigned yields an unlinked item. */
export function focusedEpicForCreate(focused: string | null): string | null {
  return focused && focused !== UNASSIGNED_EPIC ? focused : null;
}

/**
 * App-wide, per-board source of truth for the EPIC LIST sidebar. It keeps the current board's
 * items live (mirroring a view via {@link useLiveItems}) purely so it can derive, in real time,
 * (a) the Epic-typed items the sidebar lists and (b) how many items each epic has linked. It
 * also owns the sidebar's cross-view UI state — open/closed (persisted per member, like the
 * details-panel pin), which epic is "focused" for highlighting, and which epic is mid-drag so
 * views can show a drop affordance.
 *
 * Linking an item to an epic is a NARROW write done by each view's own drop handler (it needs to
 * update that view's local items), so it does not live here — but such a link never changes the
 * epic LIST anyway (only creating/deleting an epic, or retyping an item to/from Epic, does).
 */
interface EpicsCtx {
  /** The current board's Epic-typed items, live, ordered by number. */
  epics: Item[];
  /** How many items are linked to the given epic (epic_id), from the live item set. */
  linkedCount: (epicId: string) => number;
  /** An epic's completion (done / total linked items) — drives the subtle rail progress bar. */
  epicProgress: (epicId: string) => EpicProgress;
  /** How many board-visible items (excluding epics/initiatives) have NO epic — drives the Unassigned card. */
  unassignedCount: number;
  /** Sidebar open preference (persisted per member; only shown on wide screens). */
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (v: boolean) => void;
  /** Click-to-focus: the views dim items not linked to this epic. Session-only, resets on board switch. */
  focusedEpicId: string | null;
  setFocusedEpic: (id: string | null) => void;
  /** The epic currently being dragged from the sidebar (drives the rows'/cards' drop affordance). */
  draggingEpicId: string | null;
  setDraggingEpicId: (id: string | null) => void;
  reload: () => Promise<void>;
}

const Ctx = createContext<EpicsCtx | null>(null);

export const useEpics = (): EpicsCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useEpics must be used within EpicsProvider');
  return v;
};

/** The dataTransfer MIME the epic chips carry, matched by the views' drop targets. */
export const EPIC_DRAG_MIME = 'application/x-spark-epic';

/**
 * The epic id currently being dragged from the rail — a module-level singleton set synchronously at
 * dragstart (see the sidebar). We DON'T trust `dataTransfer` during the drag: WebKit/WKWebView (the
 * desktop app) is unreliable about exposing CUSTOM MIME types in `dataTransfer.types` on `dragover`,
 * and `getData` is empty until `drop` — so a guard/read against dataTransfer mid-drag silently failed
 * and no drop ever completed. This marker is the source of truth for "an epic drag is in progress".
 */
let activeDragEpicId: string | null = null;
export const setActiveDragEpic = (id: string | null): void => {
  activeDragEpicId = id;
};

/**
 * Makes an element a drop target for epic chips dragged from the sidebar (native HTML5 DnD, so it
 * never touches the views' @dnd-kit pointer-drag machinery). Returns `isOver` (an epic is hovering
 * this target) and props to spread on the element. On drop it calls `onLink(itemId, epicId)`.
 * `onLink` should be stable-ish (a view's optimistic link handler).
 */
export function useEpicDropTarget(
  itemId: string,
  onLink: (itemId: string, epicId: string) => void,
): { isOver: boolean; dropProps: { onDragOver: (e: ReactDragEvent) => void; onDragLeave: (e: ReactDragEvent) => void; onDrop: (e: ReactDragEvent) => void } } {
  const [isOver, setIsOver] = useState(false);
  // An epic drag is ours if dataTransfer advertises our MIME (Chromium) OR our synchronous marker is set
  // (WebKit, where dataTransfer.types can omit custom types during dragover).
  const isEpicDrag = (e: ReactDragEvent): boolean =>
    activeDragEpicId != null || e.dataTransfer.types.includes(EPIC_DRAG_MIME);
  const onDragOver = (e: ReactDragEvent) => {
    if (!isEpicDrag(e)) return; // not OUR epic drag (e.g. a file drag) — leave it alone
    e.preventDefault(); // REQUIRED so the drop is allowed
    e.dataTransfer.dropEffect = 'link';
    if (!isOver) setIsOver(true);
  };
  const onDragLeave = (e: ReactDragEvent) => {
    // Ignore leaves onto our own children (native DnD fires dragleave crossing into a child).
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsOver(false);
  };
  const onDrop = (e: ReactDragEvent) => {
    // Prefer dataTransfer (works cross-tab / cross-window); fall back to the marker for WebKit.
    const epicId = e.dataTransfer.getData(EPIC_DRAG_MIME) || activeDragEpicId || '';
    setIsOver(false);
    if (!epicId) return;
    e.preventDefault();
    e.stopPropagation();
    onLink(itemId, epicId);
  };
  // Clear a stuck "hovering" ring when the drag ends anywhere. Nested targets (a kanban card inside a
  // card) matter: dropping on a CHILD stops propagation, so a PARENT that lit up on dragover never sees a
  // drop/dragleave and would stay ringed. `dragend` fires on the source at the end of every drag (drop or
  // cancel) and bubbles to window, so this resets all still-lit targets. Only armed while actually over.
  useEffect(() => {
    if (!isOver) return undefined;
    const clear = () => setIsOver(false);
    window.addEventListener('dragend', clear, true);
    return () => window.removeEventListener('dragend', clear, true);
  }, [isOver]);
  return { isOver, dropProps: { onDragOver, onDragLeave, onDrop } };
}

// Fast-paint cache for the open preference (the DB per-member pref is the source of truth; this
// only avoids a first-frame flicker). Defaults to open — the sidebar is the point of the feature.
const OPEN_CACHE_KEY = 'spark-board.epicSidebarOpen';
const cachedOpen = (): boolean => {
  try {
    return localStorage.getItem(OPEN_CACHE_KEY) !== '0';
  } catch {
    return true;
  }
};
const cacheOpen = (v: boolean) => {
  try {
    localStorage.setItem(OPEN_CACHE_KEY, v ? '1' : '0');
  } catch {
    /* ignore unavailable storage */
  }
};

export const EpicsProvider = ({ children }: { children: ReactNode }) => {
  const { currentBoardId } = useAppData();
  const { me } = useIdentity();
  const { subscribeItems, reconnectEpoch } = useRealtime();
  const [items, setItems] = useState<Item[]>([]);
  const [sidebarOpen, setSidebarState] = useState<boolean>(cachedOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  // Epic focus is derived FROM the URL (`?epic=<epic key>` | "unassigned") so it survives a refresh
  // and is shareable. Not local state — that would race the async item load on restore; instead the
  // query is the source of truth, resolved to the epic's id once items arrive (null until then, or
  // if that epic is gone). The board slug is already in the path, so the param is board-scoped.
  const epicParam = searchParams.get('epic');
  const focusedEpicId = useMemo<string | null>(() => {
    if (!epicParam) return null;
    if (epicParam === EPIC_PARAM_UNASSIGNED) return UNASSIGNED_EPIC;
    // Match by key (the friendly form we write) or id (tolerate a shared/legacy link either way).
    return items.find((it) => it.type === 'epic' && (it.key === epicParam || it.id === epicParam))?.id ?? null;
  }, [epicParam, items]);
  const [draggingEpicId, setDraggingEpicId] = useState<string | null>(null);
  // Monotonic token so a stale board's item load can never overwrite the current board's.
  const reqRef = useRef(0);

  const load = useCallback(async (boardId: string) => {
    const my = ++reqRef.current;
    const res = await api.listItems({ filter: [{ name: 'board_id', op: 'eq', value: boardId }], limit: 2000 });
    if (reqRef.current === my) setItems(res.data);
  }, []);

  const reload = useCallback(async () => {
    if (currentBoardId) await load(currentBoardId);
  }, [currentBoardId, load]);

  // (Re)load on board change. Focus needs no explicit reset — it's derived from the URL, and
  // navigating to another board drops the `?epic=` query along with the rest of the old location.
  useEffect(() => {
    reqRef.current++;
    setItems([]);
    if (currentBoardId) load(currentBoardId);
  }, [currentBoardId, load]);

  // Keep the item set live so the epic list + counts stay current: an item retyped to/from Epic,
  // (un)linked, created or deleted flows in as an item event. Unlike a view's useLiveItems this
  // does NOT skip this tab's own echoes — the provider does no optimistic mutation of its own, so
  // applying its own creates/edits is exactly how the sidebar reflects changes made in this tab.
  useEffect(() => {
    if (!currentBoardId) return undefined;
    return subscribeItems((ev) => {
      if (ev.board_id !== currentBoardId) return;
      if (ev.type === 'item.upsert') {
        const it = ev.payload as Item;
        if (!it || it.board_id !== currentBoardId) return;
        setItems((cur) => {
          const idx = cur.findIndex((x) => x.id === it.id);
          if (idx === -1) return [...cur, it];
          const next = cur.slice();
          next[idx] = it;
          return next;
        });
      } else if (ev.type === 'item.delete') {
        const ids = new Set(((ev.payload as { ids?: string[] })?.ids ?? []) as string[]);
        if (ids.size) setItems((cur) => cur.filter((x) => !ids.has(x.id)));
      }
    });
  }, [currentBoardId, subscribeItems]);

  // Catch up after a genuine reconnect (events missed during the outage).
  useEffect(() => {
    if (!currentBoardId || reconnectEpoch === 0) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectEpoch]);

  // Reconcile the open preference from the DB once the actor is known (the cache already painted).
  const userTouchedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    userTouchedRef.current = false;
    api
      .getMyPreferences()
      .then((p) => {
        if (cancelled || userTouchedRef.current || typeof p.epic_sidebar_open !== 'boolean') return;
        setSidebarState(p.epic_sidebar_open);
        cacheOpen(p.epic_sidebar_open);
      })
      .catch(() => {
        /* keep the cached value */
      });
    return () => {
      cancelled = true;
    };
  }, [me?.id]);

  const setSidebarOpen = useCallback((v: boolean) => {
    userTouchedRef.current = true;
    setSidebarState(v);
    cacheOpen(v);
    api.saveMyPreferences({ epic_sidebar_open: v }).catch(() => {
      /* best-effort; the cache holds it */
    });
  }, []);
  // Toggle via a ref so the DB write never runs inside a state updater (StrictMode double-invoke).
  const openRef = useRef(sidebarOpen);
  openRef.current = sidebarOpen;
  const toggleSidebar = useCallback(() => setSidebarOpen(!openRef.current), [setSidebarOpen]);

  // Focus writes straight into the URL (the source of truth), storing the epic's KEY — not its id —
  // to keep with the friendly-URL rule; UNASSIGNED_EPIC maps to a readable sentinel; clearing drops
  // the param. `replace` keeps focus toggles out of the history stack.
  const setFocusedEpic = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!id) next.delete('epic');
          else if (id === UNASSIGNED_EPIC) next.set('epic', EPIC_PARAM_UNASSIGNED);
          else next.set('epic', items.find((it) => it.id === id)?.key ?? id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, items],
  );

  // Ordered by the sidebar rail's own key (epic_rank), falling back to creation order (number) for
  // epics never manually reordered. A drag in the sidebar writes epic_rank; the echo re-sorts here.
  // The `|| a.number` tiebreak makes the order DETERMINISTIC if two effective keys ever collide (a
  // rank landing on another epic's integer number), so it's stable across reloads and clients.
  const epics = useMemo(
    () =>
      items
        .filter((it) => it.type === 'epic')
        .sort((a, b) => (a.epic_rank ?? a.number) - (b.epic_rank ?? b.number) || a.number - b.number),
    [items],
  );
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) if (it.epic_id) m.set(it.epic_id, (m.get(it.epic_id) ?? 0) + 1);
    return m;
  }, [items]);
  const linkedCount = useCallback((epicId: string) => counts.get(epicId) ?? 0, [counts]);

  // Per-epic completion (closed / total linked items), where closed is a done- or dropped-kind status.
  // Its total equals linkedCount, so the rail's "N linked" and its progress bar always agree.
  const metaOf = useStatusMeta();
  const progressByEpic = useMemo(() => {
    const m = new Map<string, EpicProgress>();
    for (const it of items) {
      if (!it.epic_id) continue;
      const cur = m.get(it.epic_id) ?? { closed: 0, dropped: 0, total: 0 };
      cur.total++;
      const kind = statusKind(metaOf(it.status).label);
      if (isClosedKind(kind)) cur.closed++;
      if (kind === 'dropped') cur.dropped++;
      m.set(it.epic_id, cur);
    }
    return m;
  }, [items, metaOf]);
  const epicProgress = useCallback((epicId: string): EpicProgress => progressByEpic.get(epicId) ?? { closed: 0, dropped: 0, total: 0 }, [progressByEpic]);

  // Board-visible items (tasks/stories/bugs — not epics/initiatives) that carry no epic link.
  const unassignedCount = useMemo(() => items.filter((it) => !it.epic_id && !hiddenFromBoard(it)).length, [items]);

  const value = useMemo<EpicsCtx>(
    () => ({
      epics,
      linkedCount,
      epicProgress,
      unassignedCount,
      sidebarOpen,
      toggleSidebar,
      setSidebarOpen,
      focusedEpicId,
      setFocusedEpic,
      draggingEpicId,
      setDraggingEpicId,
      reload,
    }),
    [epics, linkedCount, epicProgress, unassignedCount, sidebarOpen, toggleSidebar, setSidebarOpen, focusedEpicId, setFocusedEpic, draggingEpicId, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
